import classNames from 'classnames';
import { type ChangeEvent, useCallback, useEffect, useRef } from 'react';
import type {
  AvailableService,
  CommissioningBodyOption,
  RegistrationServiceFields,
} from 'types/registration';
import { useTranslation } from 'utils/i18n';
// Constants
import {
  SERVICE_IDENTIFIER_MAX_LENGTH,
  serviceTypes,
} from '../../../constants/fieldData';
import { getYupServiceSchema } from '../../../constants/yupServiceSchema';
// Hooks
import useForm from '../../../hooks/useForm';
import useValidation from '../../../hooks/useValidation';
import type { FieldChangeEvent } from '../../../types';
import Checkbox from '../../fields/checkbox';
import SearchableSelect from '../../fields/searchableSelect';
import SelectList from '../../fields/selectList';
// Components
import Textbox from '../../fields/textbox';
import ServiceTitle from '../serviceTitle';

interface Props {
  number: number;
  serviceFields: RegistrationServiceFields;
  isOpen?: boolean;
  availableServices: AvailableService[];
  commissioningBodies: CommissioningBodyOption[];
  handleUpdateService: (fields: RegistrationServiceFields) => void;
  handleRemoveService?: (() => void) | null;
  handleToggleServiceVisibility?: () => void;
  notUnique?: boolean;
}

const Service = ({
  number,
  serviceFields,
  isOpen = true,
  availableServices,
  commissioningBodies,
  handleUpdateService,
  handleRemoveService = null,
  handleToggleServiceVisibility,
  notUnique,
}: Props) => {
  const { t } = useTranslation('common');
  const {
    fields,
    resetFields,
    handleChange: handleFieldChange,
  } = useForm(serviceFields);

  const { errors, handleChange: handleFieldValidation } = useValidation(
    fields,
    getYupServiceSchema()
  );

  const filteredServiceOptions = (type: string) => {
    let options: AvailableService[] = [];
    switch (type) {
      case 'Clinical':
        options = availableServices.filter(service => service.isClinical);
        break;
      case 'Non-Clinical':
        options = availableServices.filter(service => !service.isClinical);
        break;
      default:
        options = availableServices;
    }

    return options.map(option => {
      return { label: option.name, value: option.id };
    });
  };

  const handleChange = useCallback(
    (event: FieldChangeEvent<unknown>) => {
      handleFieldChange(event);
      handleFieldValidation(event);
    },
    [handleFieldChange, handleFieldValidation]
  );

  const headerClasses = classNames({
    'service-container__header': true,
    'service-container__header--clinical': fields.serviceType === 'Clinical',
    'service-container__header--non-clinical':
      fields.serviceType === 'Non-Clinical',
    'service-container__header--not-unique': notUnique,
  });

  const containerClasses = classNames({
    'service-container': true,
    'service-container--new': true,
    'service-container--open': isOpen,
  });

  const displayToggleClasses = classNames({
    'display-toggle': true,
    'display-toggle--collapse': isOpen,
    'display-toggle--expand': !isOpen,
  });

  useEffect(() => {
    handleUpdateService(fields);
  }, [fields, handleUpdateService]);

  const resetService = () => {
    const serviceFieldsTemplate = (prevFields: RegistrationServiceFields) =>
      Object.keys(prevFields)
        .filter(key => key !== 'serviceType')
        .reduce<Record<string, string>>(
          (prev, key) => ({ ...prev, [key]: '' }),
          {}
        );

    resetFields(prev => ({
      ...serviceFieldsTemplate(prev),
      isOpen: prev.isOpen,
      title: prev.title,
      position: prev.position,
    }));
  };

  const handleServiceTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    resetService();
    handleChange(event);
  };

  // Track initial mount as we only want to change title when included fields are updated
  const hasMountedUpdateTitle = useRef(false);

  useEffect(() => {
    const serviceTitle = () => {
      let title = '';
      if (fields.service) {
        const selectedServices = availableServices.filter(service => {
          return service.id.toString() === fields.service.toString();
        });
        if (selectedServices.length > 0) {
          title += selectedServices[0].name;
        }
      }
      if (fields.serviceIdentifier) {
        title += ` (${fields.serviceIdentifier.trim()})`;
      }
      if (fields.serviceCommissioningBody) {
        const selectedBodies = commissioningBodies.filter(body => {
          return body.value.toString() === fields.serviceCommissioningBody;
        });
        if (selectedBodies.length > 0) {
          title += ` - ${selectedBodies[0].label}`;
        }
      }

      return title;
    };

    if (hasMountedUpdateTitle.current) {
      fields.title = serviceTitle();
      handleUpdateService(fields);
    } else {
      hasMountedUpdateTitle.current = true;
    }
  }, [
    availableServices,
    commissioningBodies,
    fields,
    fields.service,
    fields.serviceIdentifier,
    fields.serviceCommissioningBody,
    handleUpdateService,
  ]);

  // When service is selected, auto-select service type if not currently selected.
  useEffect(() => {
    const selectServiceTypeIfUnselected = () => {
      if (fields.service) {
        const selectedService = availableServices.find(
          service => service.id.toString() === fields.service.toString()
        );

        if (selectedService) {
          if (!fields.serviceType) {
            const requiredServiceType = serviceTypes.find(
              serviceType =>
                serviceType.isClinical === selectedService.isClinical
            );
            const event = {
              currentTarget: {
                name: 'serviceType',
                value: requiredServiceType!.value,
                type: 'select',
              },
            };
            handleChange(event);
          }
        }
      }
    };

    selectServiceTypeIfUnselected();
  }, [availableServices, fields.serviceType, fields.service, handleChange]);

  if (fields.serviceType === 'Clinical') {
    fields.isLicensedForService = 'Yes';
  } else {
    fields.isLicensedForService = 'No';
  }

  return (
    <li className={containerClasses} data-cy="newService">
      <div
        className={headerClasses}
        data-cy="serviceContainerHeader"
        data-testid="serviceContainerHeader"
      >
        <ServiceTitle
          number={number}
          notUnique={notUnique}
          title={fields.title}
        />
        <div className="header__actions">
          {handleRemoveService && (
            <button
              onClick={handleRemoveService}
              className="button button--white-text button--small"
              data-testid="removeService"
              data-cy="removeService"
            >
              {t('registration.service_details.remove_service')}
            </button>
          )}
          <button
            type="button"
            className={displayToggleClasses}
            onClick={handleToggleServiceVisibility}
          >
            {isOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="service-container__body">
          <SelectList
            label={t('registration.service_details.fields.service_type.label')}
            placeholder={t(
              'registration.service_details.fields.service_type.label'
            )}
            name="serviceType"
            options={serviceTypes}
            value={fields.serviceType}
            error={!!errors.serviceType}
            errorMsg={errors.serviceType}
            handleChangeCallback={handleServiceTypeChange}
          />

          <SearchableSelect
            label={t('registration.service_details.fields.service.label')}
            placeholder={t('registration.service_details.fields.service.label')}
            name="service"
            options={filteredServiceOptions(fields.serviceType)}
            value={fields.service}
            error={!!errors.service}
            errorMsg={errors.service}
            onChange={handleChange}
          />

          <Textbox
            label={t(
              'registration.service_details.fields.service_identifier.label'
            )}
            name="serviceIdentifier"
            value={fields.serviceIdentifier}
            maxLength={SERVICE_IDENTIFIER_MAX_LENGTH}
            placeholder={t(
              'registration.service_details.fields.service_identifier.label'
            )}
            onChange={handleChange}
            note={t(
              'registration.service_details.fields.service_identifier.note'
            )}
            error={!!errors.serviceIdentifier}
            errorMsg={errors.serviceIdentifier}
          />

          {fields.serviceType === 'Clinical' && (
            <>
              <div className="field-multicol">
                <Textbox
                  label={t(
                    'registration.service_details.fields.service_governing_body.label'
                  )}
                  name="serviceLicensingBody"
                  value={fields.serviceLicensingBody}
                  placeholder={t(
                    'registration.service_details.fields.service_governing_body.label'
                  )}
                  error={!!errors.serviceLicensingBody}
                  errorMsg={errors.serviceLicensingBody}
                  onChange={handleChange}
                />

                <Textbox
                  label={t(
                    'registration.service_details.fields.service_registration_no.label'
                  )}
                  name="serviceRegistrationNo"
                  value={fields.serviceRegistrationNo}
                  placeholder={t(
                    'registration.service_details.fields.service_registration_no.label'
                  )}
                  error={!!errors.serviceRegistrationNo}
                  errorMsg={errors.serviceRegistrationNo}
                  onChange={handleChange}
                />
              </div>

              <Checkbox
                label={t(
                  'registration.service_details.fields.is_commissioned_service.label'
                )}
                name="isCommissionedService"
                id="isCommissionedService"
                data-testid="isCommissionedService"
                value="Yes"
                checked={fields.isCommissionedService === 'Yes'}
                onChange={handleChange}
              />

              {fields.isCommissionedService && (
                <SelectList
                  label={t(
                    'registration.service_details.fields.service_commissioning_body.label'
                  )}
                  placeholder={t(
                    'registration.service_details.fields.service_commissioning_body.label'
                  )}
                  name="serviceCommissioningBody"
                  options={commissioningBodies}
                  value={fields.serviceCommissioningBody}
                  handleChangeCallback={handleChange}
                  error={!!errors.serviceCommissioningBody}
                  errorMsg={errors.serviceCommissioningBody}
                />
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
};

export default Service;
